import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const Conduct = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Code of Conduct" 
        description="Expected behavior and professional standards"
      >
        <section className="space-y-6">
          <p>
            BlueLux's Code of Conduct outlines the behavior expected from all independent contractors. These 
            standards ensure a safe, respectful, and professional service environment for contractors and customers.
          </p>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Core Values</h2>
          <div className="space-y-4">
            <div className="border-l-4 border-primary pl-4">
              <h3 className="text-lg font-medium">Integrity</h3>
              <p className="text-muted-foreground">
                Be honest in all dealings. Never lie, cheat, or misrepresent facts to customers or management.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <h3 className="text-lg font-medium">Respect</h3>
              <p className="text-muted-foreground">
                Treat everyone—customers, coworkers, and management—with courtesy and respect, 
                regardless of differences.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <h3 className="text-lg font-medium">Accountability</h3>
              <p className="text-muted-foreground">
                Take responsibility for your work, mistakes, and actions. Own problems and find solutions.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-4">
              <h3 className="text-lg font-medium">Excellence</h3>
              <p className="text-muted-foreground">
                Strive to do your best work every day. Take pride in representing BlueLux professionally.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Professional Behavior</h2>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>Be punctual—arrive on time and complete routes as scheduled</li>
            <li>Communicate clearly and professionally in all interactions</li>
            <li>Follow all company policies and procedures</li>
            <li>Represent BlueLux positively in public and online</li>
            <li>Maintain professional boundaries with customers</li>
            <li>Report problems or concerns promptly to management</li>
            <li>Accept feedback constructively and implement improvements</li>
          </ul>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Prohibited Conduct</h2>
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-4">
            <p className="font-semibold text-destructive mb-2">Zero Tolerance - Immediate Termination</p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Theft of company property, customer property, or time</li>
              <li>Violence, threats of violence, or intimidation</li>
              <li>Working under the influence of drugs or alcohol</li>
              <li>Sexual harassment or unwanted advances toward anyone</li>
              <li>Falsifying company records or service documentation</li>
              <li>Gross insubordination or refusal to work</li>
            </ul>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Customer Property & Privacy</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Respect for Property</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Never enter customer's home without explicit permission</li>
                <li>Do not use customer's outdoor amenities (pool, furniture, etc.)</li>
                <li>Close all gates securely after entry</li>
                <li>Be careful not to damage plants, furniture, or property</li>
                <li>Report any damage you cause immediately—honesty is appreciated</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">Privacy & Confidentiality</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Never take photos of customer's property without permission</li>
                <li>Don't discuss one customer's property with other customers</li>
                <li>Keep gate codes and access information confidential</li>
                <li>Never share customer addresses or contact information</li>
                <li>Avoid commenting on customer's lifestyle, belongings, or visitors</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Honesty & Integrity</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Time Reporting</h3>
              <p className="text-muted-foreground">
                Accurately report time spent at each stop. Don't inflate time or claim services not performed. 
                GPS tracking and customer reports will identify dishonesty.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium">Service Documentation</h3>
              <p className="text-muted-foreground">
                Complete honest service reports. Never mark services complete that weren't performed. 
                Document real issues accurately—don't downplay problems or exaggerate for upsells.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium">Conflict of Interest</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Don't offer to service customers "on the side" for cash</li>
                <li>Never sell your own products to BlueLux customers</li>
                <li>Disclose if customer is family member or friend</li>
                <li>Don't accept large gifts or tips that could influence your service</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Professional Relationships</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Fellow Contractors</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Treat all contractors with respect regardless of experience level</li>
                <li>Help teammates when they need route coverage or assistance</li>
                <li>Address conflicts directly and professionally</li>
                <li>Don't gossip about other contractors or spread rumors</li>
                <li>Celebrate team successes and support each other's growth</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">Customer Relationships</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Maintain professional boundaries at all times</li>
                <li>Friendly but not overly familiar</li>
                <li>Decline personal invitations or social events</li>
                <li>Don't accept or extend friend requests on personal social media</li>
                <li>Report any inappropriate customer behavior to management</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Technology Use</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Mobile Device Use</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>No texting or phone use while driving</li>
                <li>Keep phone use during work hours to service-related activities</li>
                <li>Don't take personal calls at customer properties</li>
                <li>Set phone to silent or vibrate during service calls</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium">Social Media</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Never post photos of customer properties without permission</li>
                <li>Don't tag or mention BlueLux in negative posts</li>
                <li>Avoid posting anything that could embarrass company</li>
                <li>Report any negative social media about BlueLux to management</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Reporting Violations</h2>
          <div className="border border-border rounded-lg p-6 space-y-4">
            <p className="text-muted-foreground">
              If you witness conduct violations or have concerns about behavior, you have multiple 
              reporting options:
            </p>
            <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
              <li>Speak directly with your supervisor</li>
              <li>Contact HR or company owner</li>
              <li>Use anonymous reporting hotline: (555) 555-5555</li>
              <li>Email confidential concerns to: ethics@bluelux.com</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-4">
              <span className="font-medium">No Retaliation:</span> BlueLux prohibits retaliation against 
              anyone who reports concerns in good faith. Anyone found retaliating will be terminated.
            </p>
          </div>

          <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">Questions About Conduct?</h2>
          <p className="text-muted-foreground">
            When in doubt about whether something is appropriate, ask yourself: "Would I want my actions 
            featured on the front page of the newspaper?" If the answer is no, don't do it. When uncertain, 
            ask management for guidance before acting.
          </p>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Conduct;